import React from 'react';
import { InputText } from 'primereact/inputtext';
import './SearchBarAtom.scss';

export interface SearchBarAtomProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isSearching?: boolean;
  rightAdornment?: React.ReactNode;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** The input's accessible name. A placeholder is not a label (it disappears once typed into), so a
   * search box with no visible label says what it searches here. */
  ariaLabel?: string;
  /** Key handling on the input itself — a picker uses it for Escape (dismiss) and ArrowDown (move into its list). */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * ARIA role for the input itself. A searchable picker makes this input the `combobox`
   * that owns its list (`PickerShellMolecule`); everything else leaves it a plain textbox.
   */
  role?: 'combobox';
  /** Combobox — whether the controlled listbox is showing options. */
  ariaExpanded?: boolean;
  /** Combobox — the id of the listbox this input controls. */
  ariaControls?: string;
  /** Combobox — how the input completes. A picker filters a list, so `list`. */
  ariaAutocomplete?: 'none' | 'inline' | 'list' | 'both';
  /**
   * Combobox — the id of the active option. Focus stays in this input while the active
   * option moves, so this is the only thing that tells a screen reader which row is live.
   */
  ariaActivedescendant?: string;
  /**
   * Datum id for the search field's outer wrapper — set by callers whose prototype
   * decomposition names it. The prototype's own decomposed node is the field's
   * bordered box (icon + input + adornment), not the icon glyph alone, so this
   * attaches to the wrapper `div` rather than the `<i>`.
   */
  iconDatum?: string;
  /** Datum id for the search input itself — set by callers whose prototype decomposition names it. */
  inputDatum?: string;
}

const SearchBarAtom: React.FC<SearchBarAtomProps> = ({
  value,
  onChange,
  placeholder,
  isSearching = false,
  rightAdornment,
  className = '',
  autoFocus = false,
  disabled = false,
  onFocus,
  ariaLabel,
  role,
  ariaExpanded,
  ariaControls,
  ariaAutocomplete,
  ariaActivedescendant,
  onKeyDown,
  iconDatum,
  inputDatum,
}) => {
  return (
    <div className={`search-bar ${className}`} data-datum={iconDatum} data-kind={iconDatum ? 'icon' : undefined}>
      {isSearching ? (
        <i className="pi pi-spin pi-spinner search-bar__icon" />
      ) : (
        <i className="pi pi-search search-bar__icon" />
      )}
      <InputText
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="search-bar__input"
        autoFocus={autoFocus}
        disabled={disabled}
        onFocus={onFocus}
        aria-label={ariaLabel}
        role={role}
        aria-expanded={role === 'combobox' ? Boolean(ariaExpanded) : undefined}
        aria-controls={ariaControls}
        aria-autocomplete={ariaAutocomplete}
        aria-activedescendant={ariaActivedescendant}
        onKeyDown={onKeyDown}
        data-testid="search-bar-input"
        data-datum={inputDatum}
        data-kind={inputDatum ? 'input' : undefined}
      />
      {value && (
        <button
          type="button"
          className="search-bar__clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange('')}
          aria-label="Clear search"
          data-testid="search-bar-clear"
        >
          <i className="pi pi-times" />
        </button>
      )}
      {rightAdornment}
    </div>
  );
};

export default SearchBarAtom;
